import { logger } from '@adornis/base/logging.js';
import { ChemistryLitElement } from '@adornis/chemistry/chemistry-lit-element.js';
import { RXController } from '@adornis/chemistry/controllers/RXController.js';
import { css } from '@adornis/chemistry/directives/css.js';
import { goTo } from '@adornis/router/client/open-href.js';
import { MultipleValidationError } from '@adornis/validation/errors/MultipleValidationError.js';
import { ValidationError } from '@adornis/validation/errors/ValidationError.js';
import '@adornis/writer/x-tinymce.js';
import { g_logistikCart } from '@chancenlogistik/client/globalSubjects.js';
import { LogistikRoutes } from '@chancenlogistik/client/routes.js';
import { LogistikCart } from '@chancenwerk/db/LogistikCart.js';
import { LogistikOrderShippingDetails } from '@chancenwerk/db/LogistikOrder.js';
import '@chancenwerk/design/components/cr-checkbox.js';
import '@chancenwerk/design/components/cr-date-picker.js';
import { ChromiumDialog } from '@chancenwerk/design/components/cr-dialog.js';
import '@chancenwerk/design/components/cr-writer/cr-writer.js';
import { html, nothing } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { createRef, ref } from 'lit/directives/ref.js';
import { type LogistikComponentShippingDetails } from '../components/logistik-component-shipping-details.js';
import { placeOrder } from '../helpers/place-order.js';

/**
 * @customElement logistik-page-checkout
 */
@customElement('logistik-page-checkout')
export class LogistikPageCheckout extends ChemistryLitElement {
  @state() protected readonly _shippingDetails = new RXController(this, new LogistikOrderShippingDetails({}));
  @state() protected readonly _cart = new RXController(this, g_logistikCart);

  @state() protected readonly _errorMsg = new RXController(this, '');
  @state() protected readonly _isLoading = new RXController(this, false);

  protected _isExpress = false;

  protected readonly _shippingDetailForm = createRef<LogistikComponentShippingDetails>();

  protected async _handleFormSuccess(shippingDetails: LogistikOrderShippingDetails) {
    try {
      await placeOrder(g_logistikCart.value.items, shippingDetails, {
        isExpress: this._isExpress,
      });
    } catch (err: any) {
      this._handleError(err.message);
      return;
    }
    g_logistikCart.next(new LogistikCart({}));
    goTo(LogistikRoutes.orderSuccess);
  }

  protected _handleError(errMsg: string) {
    this._errorMsg.next(errMsg);
    this._isLoading.next(false);
  }

  override render() {
    if (!g_logistikCart.value.items || g_logistikCart.value.items.length === 0) {
      return html`
        <logistik-component-base-page-layout>
          <logistik-component-base-header
            pageTitle="Bestellung abschließen"
            backRoute=${LogistikRoutes.cart}
          ></logistik-component-base-header>
          <logistik-component-error-message> Der Warenkorb ist leer </logistik-component-error-message>
        </logistik-component-base-page-layout>
      `;
    }
    return html`
      <logistik-component-base-page-layout>
        <logistik-component-base-header
          pageTitle="Bestellung abschließen"
          backRoute=${LogistikRoutes.cart}
        ></logistik-component-base-header>
        <cr-card ${css({ width: '100%' })}>
          <cr-flex space="md">
            ${this._errorMsg.value
              ? html`<logistik-component-error-message>${this._errorMsg.value}</logistik-component-error-message>`
              : nothing}
            ${this._shippingDetailsComponent()}
            <cr-button
              .loading=${this._isLoading.value}
              @click=${async () => {
                this._isLoading.next(true);
                this._errorMsg.next('');
                if (!this._shippingDetailForm.value) {
                  this._isLoading.next(false);
                  this._errorMsg.next(
                    'Beim Bestellen ist etwas schief gelaufen. Bitte versuchen Sie es später erneut!',
                  );
                  return;
                }
                try {
                  const shippingDetails = await this._shippingDetailForm.value._form.validate();
                  if (!shippingDetails) return this._isLoading.next(false);
                  await this._handleFormSuccess(shippingDetails);
                } catch (e) {
                  this._isLoading.next(false);
                  if (e instanceof ValidationError || e instanceof MultipleValidationError) return;
                  logger.error({ err: e }, 'Es gab ein Problem beim Aufgeben der Bestellung');
                  ChromiumDialog.alert(
                    'Es gab ein Problem beim Aufgeben Ihrer Bestellung. Bitte versuchen Sie es später erneut',
                  );
                }
              }}
            >
              Bestellen
            </cr-button>
          </cr-flex>
        </cr-card>
      </logistik-component-base-page-layout>
    `;
  }

  protected _shippingDetailsComponent() {
    return html`
      <logistik-component-shipping-details
        ${ref(this._shippingDetailForm)}
        .value=${this._shippingDetails.value}
      ></logistik-component-shipping-details>
    `;
  }
}
